import '../styles/WhyDoppler.css';

const CARDS = [
  {
    id: '01',
    title: 'Institutional-Grade Yields',
    description:
      'Tap into sophisticated strategies like arbitrage and basis trading—once reserved for large financial players.',
    shape: 'cone',
  },
  {
    id: '02',
    title: 'Auto-Compounding Returns',
    description: 'Earn yields in your native asset that accrue and compound automatically—no action required.',
    shape: 'sphere',
  },
  {
    id: '03',
    title: 'Full Transparency',
    description:
      'Doppler provides realtime PoR (Proof of Reserves) which verifies that every asset is fully backed and secure—anytime, anywhere.',
    shape: 'torus',
  },
  {
    id: '04',
    title: 'Security & Audits',
    description:
      'We leverage industry-leading custody solutions to secure your assets and undergo multiple audits by top-tier security firms.',
    shape: 'cube',
  },
];

function ConeWireframe() {
  const cx = 60;
  const topY = 8;
  const baseY = 96;
  const rx = 46;
  const ry = 14;
  const rings = 5;
  return (
    <svg viewBox="0 0 120 110" className="why-shape">
      {Array.from({ length: 9 }).map((_, i) => {
        const angle = (i / 9) * Math.PI * 2;
        const x2 = cx + Math.cos(angle) * rx;
        const y2 = baseY + Math.sin(angle) * ry;
        return <line key={i} x1={cx} y1={topY} x2={x2} y2={y2} className="why-shape__line" />;
      })}
      {Array.from({ length: rings }).map((_, i) => {
        const t = (i + 1) / rings;
        const ringRx = rx * t;
        const ringRy = ry * t;
        const ringY = topY + (baseY - topY) * t;
        return (
          <ellipse
            key={i}
            cx={cx}
            cy={ringY}
            rx={ringRx}
            ry={ringRy}
            className="why-shape__line"
          />
        );
      })}
    </svg>
  );
}

function SphereWireframe() {
  const cx = 60;
  const cy = 55;
  const r = 46;
  return (
    <svg viewBox="0 0 120 110" className="why-shape">
      <circle cx={cx} cy={cy} r={r} className="why-shape__line" />
      <ellipse cx={cx} cy={cy} rx={r} ry={r * 0.35} className="why-shape__line" />
      <ellipse cx={cx} cy={cy} rx={r * 0.35} ry={r} className="why-shape__line" />
      <ellipse cx={cx} cy={cy} rx={r} ry={r * 0.7} className="why-shape__line" transform={`rotate(35 ${cx} ${cy})`} />
      <ellipse cx={cx} cy={cy} rx={r} ry={r * 0.7} className="why-shape__line" transform={`rotate(-35 ${cx} ${cy})`} />
    </svg>
  );
}

function TorusWireframe() {
  const cx = 60;
  const cy = 55;
  return (
    <svg viewBox="0 0 120 110" className="why-shape">
      <ellipse cx={cx} cy={cy} rx="48" ry="20" className="why-shape__line" />
      <ellipse cx={cx} cy={cy} rx="20" ry="48" className="why-shape__line" />
      <ellipse cx={cx} cy={cy} rx="48" ry="20" className="why-shape__line" transform={`rotate(60 ${cx} ${cy})`} />
      <ellipse cx={cx} cy={cy} rx="48" ry="20" className="why-shape__line" transform={`rotate(120 ${cx} ${cy})`} />
      <circle cx={cx} cy={cy} r="16" className="why-shape__line" />
    </svg>
  );
}

function CubeWireframe() {
  // Wireframe cube built from two offset squares plus connecting edges,
  // then sliced with internal grid lines to match the reference image.
  return (
    <svg viewBox="0 0 120 110" className="why-shape">
      <polygon points="20,30 80,30 80,90 20,90" className="why-shape__line" />
      <polygon points="40,10 100,10 100,70 40,70" className="why-shape__line" />
      <line x1="20" y1="30" x2="40" y2="10" className="why-shape__line" />
      <line x1="80" y1="30" x2="100" y2="10" className="why-shape__line" />
      <line x1="80" y1="90" x2="100" y2="70" className="why-shape__line" />
      <line x1="20" y1="90" x2="40" y2="70" className="why-shape__line" />
      <line x1="20" y1="60" x2="80" y2="60" className="why-shape__line" />
      <line x1="50" y1="30" x2="50" y2="90" className="why-shape__line" />
      <line x1="40" y1="40" x2="100" y2="40" className="why-shape__line" />
      <line x1="70" y1="10" x2="70" y2="70" className="why-shape__line" />
    </svg>
  );
}

const SHAPES = {
  cone: ConeWireframe,
  sphere: SphereWireframe,
  torus: TorusWireframe,
  cube: CubeWireframe,
};

export default function WhyDoppler({ zIndex }) {
  return (
    <section className="why-doppler" style={{ zIndex }}>
      <div className="why-doppler__topbar">
        <span className="why-doppler__brand">Doppler.finance</span>
        <span className="why-doppler__pill">
          <span className="why-doppler__pill-brace">{'{'}</span>
          Why
          <span className="why-doppler__pill-brace">{'}'}</span>
        </span>
      </div>

      <div className="why-doppler__layout">
        <h2 className="why-doppler__heading">
          <span className="why-doppler__bold">Why</span> Doppler
        </h2>

        <div className="why-doppler__cards">
          {CARDS.map((card) => {
            const Shape = SHAPES[card.shape];
            return (
              <div key={card.id} className="why-card">
                <div className="why-card__top">
                  <span className="why-card__tag">Why Doppler #{card.id}</span>
                </div>
                <h3 className="why-card__title">{card.title}</h3>
                <p className="why-card__desc">{card.description}</p>
                <div className="why-card__shape-wrap">
                  <Shape />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}